import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { I18n } from 'react-redux-i18n-lite';
import { BottomSheet } from '@netexknowledge/smart-components';
import GradebookFiltersSheetContent, { getFiltersViewTitle } from './GradebookFiltersSheetContent';
import { GRADEBOOK_SCOPE } from '../../../const';
import { getItemName } from './gradebookMobileUtils';
import { isMobileViewport } from '../../../utils/mobileUtils';

/**
 * Mobile replacement of the gradebook side bar: a summary of the selection that opens the filters in a bottom
 * sheet (book, unit and lesson lists as its second view). It also does what the side bar does while mounted on
 * desktop: picking the only book and clearing the selection on leave.
 */
const GradebookMobileFilterBar = props => {
  const { books, selectedBook, selectedUnit, selectedLesson, gradebookScope, tasksFilterParams } = props;
  const [open, setOpen] = useState(false);
  // null: the filters; 'book', 'unit' or 'lesson': that list
  const [view, setView] = useState(null);

  const propsRef = useRef(props);
  propsRef.current = props;

  useEffect(() => {
    const { setBookData, routeParams, sectionId } = propsRef.current;
    if (books && books.length === 1 && !selectedBook) {
      setBookData(routeParams.idClassroom, books[0], sectionId);
    }
    // Only when the books arrive, as GradebookSidebar.componentDidUpdate
  }, [books]);

  useEffect(
    () => () => {
      // Swapped for the desktop side bar (phone rotated): the selection stays
      if (!isMobileViewport()) return;
      const { setSelectedBook, setSelectedUnit, setSelectedLesson } = propsRef.current;
      setSelectedBook(null);
      setSelectedUnit(null);
      setSelectedLesson(null);
    },
    []
  );

  const isTasks = gradebookScope === GRADEBOOK_SCOPE.TASKS;
  let summary;
  if (isTasks) {
    const status = tasksFilterParams && tasksFilterParams.status;
    summary = [I18n.t('gradebook.scope.tasks'), status && I18n.t(status.label)];
  } else {
    summary = [selectedBook, selectedUnit, selectedLesson].map(getItemName);
    if (!selectedBook) {
      summary = [I18n.t('gradebook.select.title')];
    }
  }
  const summaryText = summary.filter(Boolean).join(' › ');

  return (
    <div className="gradebook-mobile-filters">
      <button
        type="button"
        className="gradebook-mobile-filters-button"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => {
          setView(null);
          setOpen(true);
        }}
      >
        <i className="custom-icons-filter gradebook-mobile-filters-icon" aria-hidden="true" />
        <span className="gradebook-mobile-filters-summary">{summaryText}</span>
        <i className="custom-icons-down-chevron gradebook-mobile-filters-chevron" aria-hidden="true" />
      </button>
      <BottomSheet
        open={open}
        onClose={() => setOpen(false)}
        closeLabel={I18n.t('common.close')}
        title={view ? getFiltersViewTitle(view) : I18n.t('gradebook.mobile.filters')}
        onBack={view ? () => setView(null) : undefined}
        backLabel={I18n.t('common.back')}
        fullHeight
      >
        <GradebookFiltersSheetContent {...props} view={view} onViewChange={setView} />
      </BottomSheet>
    </div>
  );
};

GradebookMobileFilterBar.propTypes = {
  books: PropTypes.array,
  selectedBook: PropTypes.object,
  selectedUnit: PropTypes.object,
  selectedLesson: PropTypes.object,
  gradebookScope: PropTypes.symbol,
  tasksFilterParams: PropTypes.object,
  routeParams: PropTypes.any,
  sectionId: PropTypes.string,
  setBookData: PropTypes.func,
  setSelectedBook: PropTypes.func,
  setSelectedUnit: PropTypes.func,
  setSelectedLesson: PropTypes.func
};

export default GradebookMobileFilterBar;
